import { useEffect, useState } from "react";
import { useVisitorContext } from "@/contexts/VisitorTrackingContext";
import { supabase } from "@/integrations/supabase/client";
import { ContentSection } from "@/components/anchor/ContentSections";
import type { LandingBlock } from "@/types/landingBlocks";

interface PersonaMapping {
  ctaOrder: string[];
  pillarOrder: string[];
}

let cachedPersonas: Record<string, PersonaMapping> | null = null;

async function loadPersonaMappings(): Promise<Record<string, PersonaMapping>> {
  if (cachedPersonas) return cachedPersonas;

  const { data } = await supabase
    .from("tanya_config" as any)
    .select("key, value")
    .eq("key", "persona_options")
    .single();

  if (!data) return {};

  const val = typeof (data as any).value === "string"
    ? JSON.parse((data as any).value)
    : (data as any).value;

  if (!Array.isArray(val)) return {};

  const map: Record<string, PersonaMapping> = {};
  for (const p of val) {
    if (p.key) {
      map[p.key] = {
        ctaOrder: p.ctaOrder || [],
        pillarOrder: p.pillarOrder || [],
      };
    }
  }
  cachedPersonas = map;
  return map;
}

/**
 * Reorders CTA buttons and content sections based on visitor's active persona.
 * Falls back to original order if no persona or no mapping defined.
 */
export function usePersonaReorder(
  ctaButtons: string[],
  contentSections: ContentSection[],
): { reorderedCtas: string[]; reorderedSections: ContentSection[] } {
  const { persona } = useVisitorContext();
  const [mappings, setMappings] = useState<Record<string, PersonaMapping>>({});

  useEffect(() => {
    loadPersonaMappings().then(setMappings);
  }, []);

  if (!persona || !mappings[persona]) {
    return { reorderedCtas: ctaButtons, reorderedSections: contentSections };
  }

  const mapping = mappings[persona];

  // Reorder CTAs: put persona-preferred ones first, keep the rest in original order
  const reorderedCtas = reorderByPreference(ctaButtons, mapping.ctaOrder);

  // Reorder content sections by matching title keywords from pillarOrder
  const reorderedSections = reorderSectionsByPillar(contentSections, mapping.pillarOrder);

  return { reorderedCtas, reorderedSections };
}

function reorderByPreference(items: string[], preferred: string[]): string[] {
  if (!preferred.length) return items;
  const ordered: string[] = [];
  for (const pref of preferred) {
    if (items.includes(pref) && !ordered.includes(pref)) ordered.push(pref);
  }
  for (const item of items) {
    if (!ordered.includes(item)) ordered.push(item);
  }
  return ordered;
}

/**
 * Applies the same persona reordering to a block array: CTA strips get their
 * buttons reordered, content-section blocks get their sections reordered.
 * Block order itself is preserved so the page structure stays intact.
 */
export function usePersonaReorderedBlocks(blocks: LandingBlock[]): LandingBlock[] {
  const allCtas = blocks.flatMap((b) => (b.type === "cta_strip" ? (b.data?.buttons ?? []) : []));
  const allSections = blocks.flatMap((b) => (b.type === "content_sections" ? (b.data?.sections ?? []) : []));
  const { reorderedCtas, reorderedSections } = usePersonaReorder(allCtas, allSections);

  return blocks.map((block) => {
    if (block.type === "cta_strip") {
      const own: string[] = block.data?.buttons ?? [];
      const ordered = reorderedCtas.filter((k) => own.includes(k));
      const missing = own.filter((k) => !ordered.includes(k));
      return { ...block, data: { ...block.data, buttons: [...ordered, ...missing] } };
    }
    if (block.type === "content_sections") {
      const own: ContentSection[] = block.data?.sections ?? [];
      const ordered = reorderedSections.filter((s) => own.includes(s));
      const missing = own.filter((s) => !ordered.includes(s));
      return { ...block, data: { ...block.data, sections: [...ordered, ...missing] } };
    }
    return block;
  });
}

function reorderSectionsByPillar(
  sections: ContentSection[],
  pillarOrder: string[],
): ContentSection[] {
  if (!pillarOrder.length || !sections.length) return sections;

  const lowerPillars = pillarOrder.map((p) => p.toLowerCase());
  const scored = sections.map((section, originalIndex) => {
    const titleLower = (section.title || "").toLowerCase();
    const linkLower = (section.link_url || "").toLowerCase();
    // Find best match index in pillarOrder
    let bestIdx = lowerPillars.length; // default: after all preferred
    for (let i = 0; i < lowerPillars.length; i++) {
      if (titleLower.includes(lowerPillars[i]) || linkLower.includes(lowerPillars[i])) {
        bestIdx = i;
        break;
      }
    }
    return { section, bestIdx, originalIndex };
  });

  scored.sort((a, b) => a.bestIdx - b.bestIdx || a.originalIndex - b.originalIndex);
  return scored.map((s) => s.section);
}
